import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { JobPart } from '../../services/types';

interface JobCoPartTableProps {
  parts: JobPart[];
  primaryPartNum: string;
}

const JobCoPartTable: React.FC<JobCoPartTableProps> = ({
  parts,
  primaryPartNum,
}) => {
  const { t } = useTranslation();

  // A job that makes one part has no co-parts. One that makes several lists
  // them all, the job's own part first: Epicor does not guarantee its
  // position in the array, so match on part number.
  const primary = primaryPartNum?.trim();
  const isPrimary = (part: JobPart) => part.partNum?.trim() === primary;
  const coParts =
    parts.length > 1
      ? [
          ...parts.filter(isPrimary),
          ...parts.filter((part) => !isPrimary(part)),
        ]
      : [];

  // Each part's share of the job's cost: its cost factor over all the parts'
  // (an even split when none is set). Labour and material can differ.
  const shareOf = (part: JobPart, field: 'lbrCostBase' | 'mtlCostBase') => {
    const total = coParts.reduce((sum, row) => sum + (row[field] || 0), 0);
    return total > 0 ? (part[field] || 0) / total : 1 / coParts.length;
  };
  const percent = (share: number) => `${Math.round(share * 1000) / 10}%`;
  const costShare = (part: JobPart) => {
    const labour = percent(shareOf(part, 'lbrCostBase'));
    const material = percent(shareOf(part, 'mtlCostBase'));
    return labour === material
      ? labour
      : t('jobTracker.costShareSplit', { labour, material });
  };

  if (coParts.length === 0) {
    return (
      <div className="bg-white rounded border border-gray-300 shadow-sm">
        <div className="bg-purple-50 px-4 py-3 border-b border-purple-200">
          <h3 className="text-lg font-bold text-purple-900">
            {t('jobTracker.coParts')} (0)
          </h3>
        </div>
        <div className="p-4 text-center text-gray-500">
          {t('jobTracker.noCoParts')}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-purple-50 px-4 py-3 border-b border-purple-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-purple-900">
          {t('jobTracker.coParts')} ({coParts.length})
        </h3>
      </div>

      {/* Table */}
      <div className="overflow-x-auto flex-grow">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
              <th className="px-4 py-3 text-left font-semibold text-gray-700">
                {t('jobTracker.part')}
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">
                {t('jobTracker.opDesc')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-28">
                {t('jobTracker.yieldPer')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-28">
                {t('jobTracker.partQty')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-28">
                {t('jobTracker.receivedQty')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-36">
                {t('jobTracker.costShare')}
              </th>
            </tr>
          </thead>
          <tbody>
            {coParts.map((part, idx) => (
              <tr
                key={idx}
                className="hover:bg-gray-50 transition-colors border-b border-gray-200"
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/parts/${encodeURIComponent(part.partNum)}`}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    {part.partNumUM || part.partNum}
                  </Link>
                  {isPrimary(part) && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap">
                      {t('jobTracker.jobsOwnPart')}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-700">
                  {part.partDescription}
                </td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium">
                  {part.partsPerOp}
                </td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium">
                  {part.partQty}
                </td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium">
                  {part.receivedQty}
                </td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium whitespace-nowrap">
                  {costShare(part)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobCoPartTable;
